// A one-way bridge from audio OUTPUT (read-aloud) to the speech-recognition
// LISTENER. While the app is talking, its own voice comes out the speaker and
// the microphone would hear it — the recognizer then "hears" the app read a
// hint and mistakes those words for a spoken command. To avoid that entirely we
// SUSPEND the recognition engine while any speech is playing, so there is
// nothing for it to mishear, and resume it once the app goes quiet.
//
// This lives in its own tiny module (rather than a React context) so the two
// otherwise-independent providers — SpeakProvider (output) and VoiceProvider /
// useSpeechRecognition (input) — can talk without being wired together, and
// without causing re-renders as speech starts and stops.
//
// Reference counted: each in-flight utterance takes one hold; the mic only
// resumes when the LAST hold is released. That keeps a replaced utterance
// (SpeakProvider cancels the previous one before speaking the next) from
// briefly dropping the count to zero and re-opening the mic mid-sentence.

type GateListener = (suspended: boolean) => void;

const listeners = new Set<GateListener>();
let holds = 0;

function emit() {
  const suspended = holds > 0;
  for (const l of listeners) l(suspended);
}

/**
 * Subscribe to output start/stop. The listener is called with `true` when the
 * app starts speaking and `false` when it falls silent, and immediately once on
 * subscribe to sync to the current state. Returns an unsubscribe fn.
 */
export function subscribeMicGate(l: GateListener): () => void {
  listeners.add(l);
  l(holds > 0);
  return () => {
    listeners.delete(l);
  };
}

/** Output is starting — suspend the mic. Balance every call with `releaseMic`. */
export function holdMic(): void {
  holds += 1;
  if (holds === 1) emit();
}

/** One output finished — resume the mic once all holds are released. */
export function releaseMic(): void {
  holds = Math.max(0, holds - 1);
  if (holds === 0) emit();
}
